import { gql, useQuery } from '@apollo/client';
import { Spinner } from '@apollo/orbit';
import { GraphQLSchema } from 'graphql';
import { produce } from 'immer';
import { uniqBy } from 'lodash';
import React, { useCallback, useState } from 'react';

import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { GraphRef } from '../../hooks/useGraphRef';
import { SchemaTagDiffFragment } from '../schemaTagDiffFragment/SchemaTagDiffFragment';

import { ChangeLogPageLayout } from './changeLogPageLayout/ChangeLogPageLayout';

const ChangeLogPageQuery = gql<
  GraphQLTypes.ChangeLogPage,
  GraphQLTypes.ChangeLogPageVariables
>`
  query ChangeLogPage(
    $graphVariant: String!
    $graphId: ID!
    $historyOffset: Int
    $historyLimit: Int!
    $includeUnchanged: Boolean!
  ) {
    service(id: $graphId) {
      id
      schemaTag(tag: $graphVariant) {
        id
        history(
          offset: $historyOffset
          limit: $historyLimit
          includeUnchanged: $includeUnchanged
        ) {
          id
          ...SchemaTagDiffFragment
        }
      }
    }
  }
  ${SchemaTagDiffFragment}
`;

export const ChangeLogOverviewPage = ({
  graphRef,
  graphqlSchema,
}: {
  graphRef: GraphRef;
  graphqlSchema: GraphQLSchema;
}) => {
  const { data, loading, error, fetchMore } = useQuery(ChangeLogPageQuery, {
    variables: {
      graphVariant: graphRef.graphVariant,
      graphId: graphRef.graphId,
      historyLimit: 10,
      historyOffset: 0,
      includeUnchanged: false,
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  const [hasMore, setHasMore] = useState<'maybe' | false>('maybe');

  const handleRequestMoreChanges = useCallback(() => {
    fetchMore({
      variables: {
        historyOffset: data?.service?.schemaTag?.history.length,
      },
      updateQuery: (
        prevResult: GraphQLTypes.ChangeLogPage,
        { fetchMoreResult: newResults },
      ) => {
        const newHistory = newResults?.service?.schemaTag?.history;
        if (!newResults || !newHistory || newHistory.length === 0) {
          setHasMore(false);
          return prevResult;
        }

        return produce(prevResult, (draftResults) => {
          if (!draftResults.service?.schemaTag?.history) return;
          // eslint-disable-next-line no-param-reassign
          draftResults.service.schemaTag.history = uniqBy(
            draftResults.service.schemaTag.history.concat(newHistory),
            (historyItem) => historyItem.id,
          );
        });
      },
    });
  }, [data, fetchMore]);

  if (error) {
    throw error;
  }

  if (loading) {
    return (
      <div className="h-full" style={{ display: 'grid', placeItems: 'center' }}>
        <Spinner />
      </div>
    );
  }

  return (
    <ChangeLogPageLayout
      graphqlSchema={graphqlSchema}
      schemaHistory={data?.service?.schemaTag?.history}
      onRequestMoreChanges={handleRequestMoreChanges}
      hasMore={hasMore}
    />
  );
};
